import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { tokens } from '@fhs/ui';
import {
  actions,
  selectors,
  useAppDispatch,
  useAppSelector,
} from '@fhs-legacy/frontend/src/state/global-state';
import { REGIONS } from '@fhs-legacy/frontend/src/state/intl/types';
import { IAppliedRewards } from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { useLoyaltyUser } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-loyalty-user';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { region } from '@fhs-legacy/frontend/src/utils/environment';
import { CART_PAGE } from '@fhs-legacy/frontend/src/utils/test-ids';

import AddMoreItemsButton from '../components/cart-components/add-more-items-button';
import { CartChallengeInlineAlert } from '../components/cart-components/challenge-inline-alert';
import ContinueButton from '../components/cart-components/continue-button';
import { CartDonationContainer } from '../components/cart-components/donations';
import { CartEmptyState } from '../components/cart-components/empty-state';
import CartItemsList from '../components/cart-components/item-list';
import { CartOfferItem } from '../components/cart-components/offer-item';
import { CartOffersAndPromosSections } from '../components/cart-components/offers-and-promos-section';
import { RewardNotAppliedAlert } from '../components/cart-components/reward-not-applied-alert';
import { CartSubtotals } from '../components/cart-components/subtotal';
import SavingsWithOffer from '../components/savings-with-offer';
import { usePreloadPaymentMethods } from '../payment/hooks';
import { useCartValues } from '../state/cart-store';
import { calculateBaseTotal } from '../state/cart-store/pricing-cart/compute-cart-total';

export function CartReview() {
  const { formatMessage } = useIntl();
  usePreloadPaymentMethods();
  const dispatch = useAppDispatch();
  const { loyaltyUser } = useLoyaltyUser();
  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const appliedLoyaltyRewards: IAppliedRewards = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewards
  );
  const { cartEntries } = useCartValues();
  const { calculatedCartTotal } = useOrderContext();

  const hasAnyRewardApplied = Object.values(appliedLoyaltyRewards).some(
    reward => reward?.timesApplied > 0
  );

  const baseTotal = useMemo(() => calculateBaseTotal(cartEntries), [cartEntries]);
  const savings = useMemo(() => baseTotal - calculatedCartTotal, [baseTotal, calculatedCartTotal]);
  const donationAmount = useMemo(
    () => cartEntries.find(entry => entry.isDonation)?.price || 0,
    [cartEntries]
  );
  const effectiveRegion = region();
  const isCanadaStore = effectiveRegion === REGIONS.CA;
  const isCartSubTotalUndefined = calculatedCartTotal === undefined || calculatedCartTotal === null;
  const isBelowRewardMinimumSubtotal =
    !isCanadaStore &&
    hasAnyRewardApplied &&
    !isCartSubTotalUndefined &&
    calculatedCartTotal - donationAmount < 1;
  const appliedRewardEntry = Object.entries(appliedLoyaltyRewards).find(
    ([, reward]) => reward?.timesApplied > 0
  );
  const appliedRewardCartId = appliedRewardEntry?.[0];
  const appliedReward = appliedRewardEntry?.[1];
  const appliedRewardCartEntry = cartEntries.find(
    entry => appliedRewardCartId && entry.cartId === appliedRewardCartId
  );
  const appliedRewardItemName = appliedRewardCartEntry?.name || '';
  const rewardMinimumMessage = useMemo(() => {
    if (!isBelowRewardMinimumSubtotal) {
      return '';
    }
    const currentValue = Math.max(calculatedCartTotal - donationAmount, 0);
    const targetValue = 1;
    return formatMessage(
      { id: 'incentiveFeedbackSubtotal' },
      {
        incentiveType: 'reward',
        currentValue: `${(currentValue / 100).toFixed(2)}`,
        targetValue: `${(targetValue / 100).toFixed(2)}`,
        calculatedValue: `${((targetValue - currentValue) / 100).toFixed(2)}`,
      }
    );
  }, [calculatedCartTotal, donationAmount, formatMessage, isBelowRewardMinimumSubtotal]);

  const scrollViewRef = useRef<ScrollView>(null!);
  const [isBadContinueAttempt, setIsBadContinueAttempt] = useState(false);

  if (cartEntries.filter(entry => !entry.isDonation).length === 0 && calculatedCartTotal === 0) {
    return <CartEmptyState />;
  }

  return (
    <View style={styles.page} testID={CART_PAGE}>
      <ScrollView
        ref={scrollViewRef}
        accessibilityRole="scrollbar"
        accessibilityLabel="Cart contents"
      >
        <CartDonationContainer
          isBadContinueAttempt={isBadContinueAttempt}
          setIsBadContinueAttempt={setIsBadContinueAttempt}
        />

        <CartItemsList />
        <CartChallengeInlineAlert />
        <AddMoreItemsButton />

        {isBelowRewardMinimumSubtotal && (
          <View style={styles.rewardErrorContainer} accessibilityRole="alert">
            <RewardNotAppliedAlert
              title="Reward Not Applied"
              subtitle={appliedRewardItemName}
              message={rewardMinimumMessage}
              onRemove={
                appliedRewardCartId && appliedReward?.rewardBenefitId
                  ? () => {
                      dispatch(actions.loyalty.setIsPricingRewardApplication(true));
                      dispatch(
                        actions.loyalty.unApplyReward({
                          cartId: appliedRewardCartId,
                          rewardBenefitId: appliedReward.rewardBenefitId,
                          loyaltyUser,
                        })
                      );
                    }
                  : undefined
              }
            />
          </View>
        )}

        {selectedOffer ? <CartOfferItem /> : <CartOffersAndPromosSections />}

        <CartSubtotals
          baseTotal={baseTotal - donationAmount}
          grandTotal={calculatedCartTotal - donationAmount}
        />
      </ScrollView>

      <SafeAreaView>
        {(selectedOffer || hasAnyRewardApplied) && <SavingsWithOffer savings={savings} />}
        <ContinueButton
          scrollViewRef={scrollViewRef}
          setIsBadContinueAttempt={setIsBadContinueAttempt}
        />
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: 'white',
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: 1,
    flex: 1,
  },
  rewardErrorContainer: {
    marginHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
  },
});
